import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactGA from 'react-ga';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { saveAs } from 'file-saver';

import {
  selectedSurvey as survey,
  setIndicatorInfoObj,
  surveys,
  selectedIndicator as indicator,
} from '../../store/slices/modelSurfaceSlice';
import IndicatorTable from './indicatorTable';
import store from '../../store/store';

import './ModelSurfaceTable.css';

// Adds Window Interface
declare global {
  interface Window {
    env: any;
    sendGaEvent: any;
  }
}

ReactGA.initialize(window.env.GA_TRACKING_ID);

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '600px',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export default function ModelSurfaceTable() {
  const tableHead = ['Map', 'Survey', 'Indicator', 'Options'];
  const selectedSurvey = useSelector(survey);
  const selectedIndicator = useSelector(indicator);
  const allSurveys = useSelector(surveys);
  const [indicatorList, setIndicatorList] = useState([]);
  const [openIndicatorModal, setOpenIndicatorModal] = useState(false);
  const [openMapModal, setOpenMapModal] = useState(false);
  const [mapURL, setMapURl] = useState('');

  useEffect(() => {
    //Check to see if single country is selected,and add a extra survey name key
    if (Object.keys(selectedSurvey).length > 1) {
      setIndicatorList(
        selectedSurvey.indicators.map((indicator) => {
          return { ...indicator, survey_name: selectedSurvey.name };
        })
      );
    } else {
      // if all countries selected , then add a extra survey name key to indicator obj
      //in case of ALL COUNTRIES and ONE INDICATOR
      if (Object.keys(selectedIndicator).length > 1) {
        setIndicatorList(
          allSurveys
            .filter((survey) => survey.indicators.find((indicator) => indicator.Label === selectedIndicator.Label))
            .map((survey) => {
              let obj = survey.indicators.find((indicator) => indicator.Label === selectedIndicator.Label);
              return { ...obj, survey_name: survey.name };
            })
        );
      }
      //in case of ALL COUNTRIES and ALL INDICATORS
      else if (Object.keys(selectedIndicator).length === 1 && Object.keys(selectedSurvey).length === 1) {
        let allSurveyObjArr = allSurveys.map((survey) => {
          return survey.indicators.map((indicator) => {
            return { ...indicator, survey_name: survey.name };
          });
        });

        allSurveyObjArr = allSurveyObjArr.flat();

        setIndicatorList(allSurveyObjArr);
      }
    }
  }, [selectedSurvey]);

  function saveImage(url, name) {
    let img = new Image();
    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');
    img.crossOrigin = 'Anonymous';

    img.onload = function () {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(function (blob) {
        saveAs(blob, name);
      });
    };

    //To solve CORS issue on Chrome and safari, replace https:// with http://
    img.src = url.replace(/^https:\/\//i, 'http://');
  }

  return (
    <div className='table-container model-surface-table'>
      <table>
        <thead>
          <tr>
            {tableHead.map((headText, i) => {
              return (
                <th className='table-head' key={`${headText}_${i}`}>
                  <div className='head-text'>{headText}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {indicatorList.map((indicator, indicatorIndex) => {
            return (
              <tr key={indicatorIndex}>
                <td>
                  <div
                    className='thumbnail'
                    style={{ margin: '0px' }}
                    onClick={() => {
                      setMapURl(indicator.image_url);
                      setOpenMapModal(true);
                    }}
                  >
                    <img src={indicator.image_url} />
                  </div>
                </td>
                <td>{indicator.survey_name}</td>
                <td>{indicator.Label}</td>
                <td>
                  <button
                    title='Click to select indicator'
                    className='icon info blue'
                    onClick={() => {
                      store.dispatch(setIndicatorInfoObj(indicator));
                      setOpenIndicatorModal(true);
                    }}
                  />
                  <button
                    title='Download map image'
                    className='icon download-image blue'
                    onClick={() => {
                      //window.open(indicator.image_url);
                      saveImage(indicator.image_url, indicator.image);
                    }}
                  />
                  <button
                    title='Download Zipped Folder'
                    className='icon download blue'
                    onClick={() => {
                      ReactGA.ga('send', 'event', 'modeled-surfaces', 'download', indicator.tracking_string);
                      window.sendGaEvent('modeled-surfaces', 'download', indicator.tracking_string);
                      window.open(indicator.zip_url);
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Modal
        open={openIndicatorModal}
        onClose={() => {
          setOpenIndicatorModal(false);
        }}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <div
            className='close absolute'
            onClick={() => {
              setOpenIndicatorModal(false);
            }}
          >
            {' '}
          </div>
          <IndicatorTable />
        </Box>
      </Modal>

      <Modal
        open={openMapModal}
        onClose={() => {
          setOpenMapModal(false);
        }}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <div
            className='close absolute'
            onClick={() => {
              setOpenMapModal(false);
            }}
          ></div>
          <img width='500' height='650' src={mapURL} />
          <div className='map-legend' style={{ border: '1px solid black' }}>
            <img width='133' height='57' src='app/images/legend.png' className='map-legend__image-label' />
          </div>
        </Box>
      </Modal>
    </div>
  );
}
